import { useState, type ReactNode } from 'react'
import type { Lead, EtapaPipeline, PipelineSubestado } from '@/lib/types'
import { IconChevronDown, IconChevronRight } from '@/components/icons/Icons'
import LeadCard from './LeadCard'
import {
  IconTarget, IconPhone, IconSearch, IconFileText, IconBarChart, IconCheckCircle,
} from '@/components/icons/Icons'

interface PipelineColumn {
  etapa: EtapaPipeline
  label: string
  color: string
  /* El ícono vivía arriba, en una fila de seis tarjetas que repetía los
     mismos seis números que ya dice cada columna. Las tarjetas se fueron y el
     ícono se quedó, al lado del nombre de su etapa: ahí dice qué es esa
     columna sin ocupar una franja de la pantalla. */
  icono: ReactNode
}

/* Las seis etapas se distinguen entre si; ninguna es "mejor" que la anterior
   por su color, asi que van en la escala categorica y no en el semaforo. */
const COLUMNS: PipelineColumn[] = [
  { etapa: 'lead',        label: 'Lead',        color: 'var(--cat-4)', icono: <IconTarget size={14} /> },
  { etapa: 'contactado',  label: 'Contactado',  color: 'var(--cat-1)', icono: <IconPhone size={14} /> },
  { etapa: 'diagnostico', label: 'Diagnóstico', color: 'var(--cat-3)', icono: <IconSearch size={14} /> },
  { etapa: 'cotizado',    label: 'Cotizado',    color: 'var(--cat-5)', icono: <IconFileText size={14} /> },
  { etapa: 'negociacion', label: 'Negociación', color: 'var(--cat-7)', icono: <IconBarChart size={14} /> },
  { etapa: 'cerrado',     label: 'Cerrado',     color: 'var(--cat-2)', icono: <IconCheckCircle size={14} /> },
]

interface PipelineBoardProps {
  leads: Lead[]
  /** Los pasos que configuró el dueño del tablero, de todas las etapas. */
  subestados?: PipelineSubestado[]
  onStageChange: (leadId: number, newStage: EtapaPipeline, subestado?: string | null) => void
  onLeadClick: (lead: Lead) => void
  selectedIds: Set<number>
  onToggleSelect: (id: number) => void
}

export default function PipelineBoard({
  leads, subestados = [], onStageChange, onLeadClick, selectedIds, onToggleSelect,
}: PipelineBoardProps) {
  /*
    La columna que está abierta en sus pasos. Una sola a la vez: con todas
    abiertas el tablero pasa de seis columnas a veinte y deja de leerse de un
    vistazo, que es lo único que un Kanban hace mejor que una tabla.

    Vive en `localStorage` y no en la base: es una preferencia de vista de
    esta persona en este navegador, no un dato de la oportunidad.
  */
  const [abierta, setAbierta] = useState<EtapaPipeline | null>(() => {
    try {
      return (localStorage.getItem('pipeline_etapa_abierta') as EtapaPipeline) || null
    } catch { return null }
  })

  function alternarAbierta(etapa: EtapaPipeline) {
    const siguiente = abierta === etapa ? null : etapa
    setAbierta(siguiente)
    try {
      if (siguiente) localStorage.setItem('pipeline_etapa_abierta', siguiente)
      else localStorage.removeItem('pipeline_etapa_abierta')
    } catch { /* sin localStorage el tablero funciona igual, sin recordar */ }
  }

  const [dragOverCol, setDragOverCol] = useState<EtapaPipeline | null>(null)
  const [dragOverPaso, setDragOverPaso] = useState<string | null>(null)

  /* El color de cada paso, por nombre: lo necesitan la cabecera de la
     subcolumna y el chip de la tarjeta, que no reciben la lista entera. */
  const colorDe = new Map(subestados.map(p => [`${p.etapa}:${p.nombre}`, p.color || null]))

  function getDaysInStage(lead: Lead): number {
    if (!lead.fecha_primer_contacto) return 0
    return Math.max(0, Math.floor((Date.now() - new Date(lead.fecha_primer_contacto).getTime()) / 86400000))
  }

  function handleDragOver(e: React.DragEvent, etapa: EtapaPipeline) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverCol(etapa)
  }

  function handleDrop(e: React.DragEvent, nuevoEtapa: EtapaPipeline) {
    e.preventDefault()
    setDragOverCol(null)
    const leadId = Number(e.dataTransfer.getData('text/plain'))
    const lead = leads.find(l => l.id === leadId)
    if (!lead || lead.etapa === nuevoEtapa) return
    onStageChange(leadId, nuevoEtapa)
  }

  /*
    Soltar en una subcolumna escribe etapa Y paso en una sola llamada. Son el
    mismo movimiento: mandarlos por separado deja un instante en que el lead
    está en la etapa nueva con el paso viejo, que el backend limpiaría.
  */
  function handleDropPaso(e: React.DragEvent, etapa: EtapaPipeline, paso: string | null) {
    e.preventDefault()
    e.stopPropagation()
    setDragOverCol(null)
    setDragOverPaso(null)
    const leadId = Number(e.dataTransfer.getData('text/plain'))
    const lead = leads.find(l => l.id === leadId)
    if (!lead) return
    if (lead.etapa === etapa && (lead.subestado || null) === paso) return
    onStageChange(leadId, etapa, paso)
  }

  return (
    <div className="crm-pipeline-board">
      {COLUMNS.map(col => {
        const colLeads = leads.filter(l => l.etapa === col.etapa)
        const pasos = subestados.filter(p => p.etapa === col.etapa && p.activo)
        const desplegada = abierta === col.etapa && pasos.length > 0
        return (
          <div
            key={col.etapa}
            className={`crm-pipeline-column${dragOverCol === col.etapa ? ' drag-over' : ''}`}
            onDragOver={e => handleDragOver(e, col.etapa)}
            onDragLeave={() => setDragOverCol(null)}
            onDrop={e => handleDrop(e, col.etapa)}
          >
            <div className="crm-pipeline-column-header">
              <div className="crm-pipeline-column-bar" style={{ background: col.color }} />
              <div className="crm-pipeline-column-header__inner">
                {/* Ícono y nombre son UNA cosa y van juntos a la izquierda: con
                    los tres sueltos, el `space-between` dejaba el nombre
                    flotando en el medio de la cabecera. */}
                <span className="crm-pipeline-column-etiqueta">
                  <span className="crm-pipeline-column-icono" style={{ color: col.color }}>{col.icono}</span>
                  <span className="crm-pipeline-column-title">{col.label}</span>
                  {/* Sólo donde hay pasos configurados: una flecha que no
                      despliega nada es una promesa incumplida. */}
                  {pasos.length > 0 && (
                    <button
                      type="button"
                      className="crm-pipeline-column-desplegar"
                      onClick={() => alternarAbierta(col.etapa)}
                      title={desplegada ? 'Ver la etapa entera' : `Ver los ${pasos.length} pasos`}
                    >
                      {desplegada ? <IconChevronDown size={13} /> : <IconChevronRight size={13} />}
                    </button>
                  )}
                </span>
                <span className="crm-pipeline-column-count" style={{ color: col.color }}>{colLeads.length}</span>
              </div>
            </div>
            <div className="crm-pipeline-column-body">
              {desplegada ? (
                /*
                  Desplegada: un bloque por paso, más uno al final para los que
                  no tienen ninguno. Ese último no es decorativo — es donde cae
                  todo lo que entró antes de que existieran los pasos, y
                  esconderlo haría desaparecer leads del tablero.
                */
                <>
                  {[...pasos.map(p => p.nombre), null].map(paso => {
                    const delPaso = colLeads.filter(l => (l.subestado || null) === paso)
                    const clave = paso ?? '__sin_paso'
                    return (
                      <div
                        key={clave}
                        className={`crm-pipeline-paso${dragOverPaso === `${col.etapa}:${clave}` ? ' drag-over' : ''}`}
                        onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverPaso(`${col.etapa}:${clave}`) }}
                        onDragLeave={() => setDragOverPaso(null)}
                        onDrop={e => handleDropPaso(e, col.etapa, paso)}
                      >
                        <div className="crm-pipeline-paso__cabecera">
                          <span className="crm-pipeline-paso__nombre">
                            <span
                              className="crm-pipeline-paso__punto"
                              style={{ background: paso ? `var(--${colorDe.get(`${col.etapa}:${paso}`) || 'cat-8'})` : 'var(--veil-25)' }}
                            />
                            {paso ?? 'Sin paso'}
                          </span>
                          <span>{delPaso.length}</span>
                        </div>
                        {delPaso.map(lead => (
                          <LeadCard
                            key={lead.id}
                            lead={lead}
                            colorPaso={colorDe.get(`${lead.etapa}:${lead.subestado}`)}
                            daysInStage={getDaysInStage(lead)}
                            onClick={() => onLeadClick(lead)}
                            borderColor={col.color}
                            isSelected={selectedIds.has(lead.id)}
                            onToggleSelect={onToggleSelect}
                          />
                        ))}
                      </div>
                    )
                  })}
                </>
              ) : (
                <>
                  {colLeads.map(lead => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      colorPaso={colorDe.get(`${lead.etapa}:${lead.subestado}`)}
                      daysInStage={getDaysInStage(lead)}
                      onClick={() => onLeadClick(lead)}
                      borderColor={col.color}
                      isSelected={selectedIds.has(lead.id)}
                      onToggleSelect={onToggleSelect}
                    />
                  ))}
                  {colLeads.length === 0 && <div className="kanban-empty"><span>Sin leads</span></div>}
                </>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
